import React, {useCallback, useEffect, useState} from 'react';
import {createStyles, makeStyles, Theme, useTheme} from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import {debounce} from "debounce";
import {
  Avatar,
  FormControl,
  List,
  ListItem,
  MenuItem,
  Select,
  Switch
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Slider from "@material-ui/core/Slider";
import {useSelector, useDispatch} from 'react-redux';
import {
  ColorMapKey, ColorType,
  selectCloudDistance, changeCloudDistance,
  selectOpacity, changeOpacity,
  selectOrthoOpacity, changeOrthoOpacity,
  selectCloudSize, changeCloudSize,
  selectHeightClip, changeHeightClip,
  selectIntensityClip, changeIntensityClip,
  selectColorMap, changeColorMap,
  selectColorType, changeColorType,
  selectDynamicSize, changeDynamicSize,
  selectAvailableClassifications, changeClassificationVisibility,
  selectClassificationVisibilities,
  changeCircularPoints, selectCircularPoints,
  changeArrowMarkers, selectArrowMarkers, MarkerNavType
} from './redux/settingsSlice';
import {colorMaps} from "./viewer/textures/colormaps";
import {getClassificationColors} from "./classifications";
import { isDevMode } from './electron-modules';
import { selectOrthos } from './redux/assetsSlice';

const drawerWidth = 280;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      display: "none"
    },
    drawer: {
      width: 0,
    },
    drawerPaper: {
      width: drawerWidth,
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
      justifyContent: 'flex-start',
    },
    list: {
      width: '100%',
      maxWidth: 360,
      backgroundColor: theme.palette.background.paper,
    },
    listItem: {
      width: '100%',
      paddingRight: '5px',
      marginBottom: theme.spacing(0.5)
    },
    formControl: {
      width: '100%',
      marginBottom: theme.spacing(1)
    },
    switch: {
      marginLeft: 0
    },
    colorPreview: {
      height: theme.spacing(3),
      width: theme.spacing(3),
      marginRight: theme.spacing(1),
    },
    img: {
      objectFit: 'fill',
      transform: 'rotate(-90deg)'
    },
    classificationPreview: {
      height: theme.spacing(1.25),
      width: theme.spacing(1.25),
      backgroundColor: '#bbb',
      borderRadius: '50%',
      borderColor: '#000000',
      borderStyle: 'solid',
      borderWidth: '1px',
      marginRight: theme.spacing(1),
      display: 'inline-block'
    },
    switchLabel: {
      display: "inline-block"
    },
    switchElement: {
      float: "right"
    },
    boldTypography: {
      fontWeight: 'bold',
    }
  }),
);

export const SettingsDrawer = (props) => {
  const viewer = props.viewer;

  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();
  const colorMap = useSelector(selectColorMap);
  const colorType = useSelector(selectColorType);
  const dynamicSize = useSelector(selectDynamicSize);
  const circularPoints = useSelector(selectCircularPoints);
  const arrowMarkers = useSelector(selectArrowMarkers);
  const availableClassifications = useSelector(selectAvailableClassifications);
  const classificationVisibilities = useSelector(selectClassificationVisibilities);
  const [classificationColors, setClassificationColors] = useState(Array(256).fill('#ffffff'));
  const orthos = useSelector(selectOrthos);

  const getClassifications = (classifications) => {
    if (classifications.length === 0) {
      return <div>
        <ListItem>
          <Typography gutterBottom variant="subtitle2">
            No points loaded
          </Typography>
        </ListItem>
      </div>;
    }

    return classifications.map(classification => (
      <ListItem key={classification.id}>
        <div className={classes.listItem}>
          <div>
            <span
              className={classes.classificationPreview}
              style={{
                backgroundColor: classificationColors[classification.id]
              }}
            />

            <Typography
              gutterBottom
              variant="subtitle2"
              className={classes.switchLabel}
            >
              {`${classification.name} (${classification.id})`}
            </Typography>

            <Switch
              checked={classification.visible}
              className={classes.switchElement}
              size="small"
              color="secondary"
              title="Note: This will have no effect for raw las files"
              onChange={() => {
                dispatch(changeClassificationVisibility(classification.id));
              }}
            />

          </div>
        </div>
      </ListItem>
    ));
  };

  const dispatchInterval = 1000;

  /** Cloud Opacity */
  const opacity = useSelector(selectOpacity);
  const [opacitySliderValue, setOpacitySlider] = useState(0);
  const saveOpacity = useCallback(
    debounce((value) => {
      dispatch(changeOpacity(value));
    }, dispatchInterval), []);

  /** Ortho Opacity */
  const orthoOpacity = useSelector(selectOrthoOpacity);
  const [orthoOpacitySliderValue, setOrthoOpacitySlider] = useState(0);
  const saveOrthoOpacity = useCallback(
    debounce((value) => {
      dispatch(changeOrthoOpacity(value));
    }, dispatchInterval), []);

  /** Cloud Distance */
  const cloudDistance = useSelector(selectCloudDistance);
  const [distanceSliderValue, setDistanceSlider] = useState(0);
  const saveCloudDistance = useCallback(
    debounce((value) => {
      dispatch(changeCloudDistance(value));
    }, dispatchInterval), []);

  /** Cloud Size */
  const cloudSize = useSelector(selectCloudSize);
  const [cloudSizeSlider, setCloudSizeSlider] = useState(0);
  const saveCloudSize = useCallback(
    debounce((value) => {
      dispatch(changeCloudSize(value));
    }, dispatchInterval), []);

  /** Height Range */
  const heightClip = useSelector(selectHeightClip);
  const [heightClipSlider, setHeightClipSlider] = useState({min: 0,max: 0});
  const saveHeightClip = useCallback(
    debounce((value) => {
      dispatch(changeHeightClip(value));
    }, dispatchInterval), []);

  /** Intensity Range */
  const intensityClip = useSelector(selectIntensityClip);
  const [intensityClipSlider, setIntensityClipSlider] = useState({min: 0,max: 0});
  const saveIntensityClip = useCallback(
    debounce((value) => {
      dispatch(changeIntensityClip(value));
    }, dispatchInterval), []);

  const updateOpacity = (value) => {
    setOpacitySlider(value);
    viewer?.setPointCloudOpacity(value);
  };

  const updateOrthoOpacity = (value) => {
    setOrthoOpacitySlider(value);
    viewer?.setOrthoOpacity(value);
  };

  const updateDistance = (value) => {
    setDistanceSlider(value);
    viewer?.setPointCloudDistance(value);
  };

  const updateCloudSize = (value) => {
    setCloudSizeSlider(value);
    viewer?.setPointCloudSize(value);
  };

  const updateHeightClip = (value) => {
    setHeightClipSlider(value);
    viewer?.setPointCloudHeightClip(value);
  };

  const updateIntensityClip = (value) => {
    setIntensityClipSlider(value);
    viewer?.setPointCloudIntensityClip(value);
  };

  useEffect(() => {
    updateOpacity(opacity);
  }, [viewer, opacity]);

  useEffect(() => {
    updateOrthoOpacity(orthoOpacity);
  }, [viewer, orthoOpacity]);

  useEffect(() => {
    updateDistance(cloudDistance);
  }, [viewer, cloudDistance]);

  useEffect(() => {
    updateCloudSize(cloudSize);
  }, [viewer, cloudSize]);

  useEffect(() => {
    updateHeightClip(heightClip);
  }, [viewer, heightClip]);

  useEffect(() => {
    updateIntensityClip(intensityClip);
  }, [viewer, intensityClip]);

  useEffect(() => {
    viewer?.setVisibleClassifications(classificationVisibilities);
  }, [viewer, classificationVisibilities]);

  useEffect(() => {
    viewer?.setPointCloudColorMap(colorMap);
  }, [viewer, colorMap]);

  useEffect(() => {
    viewer?.setPointCloudColorType(colorType);
  }, [viewer, colorType]);

  useEffect(() => {
    viewer?.setPointCloudDynamicSize(dynamicSize);
  }, [viewer, dynamicSize]);

  useEffect(() => {
    viewer?.setPointCloudCircularPoints(circularPoints);
  }, [viewer, circularPoints]);

  useEffect(() => {
    viewer?.setNavigationType(arrowMarkers);
  }, [viewer, arrowMarkers]);

  useEffect(() => {
    getClassificationColors().then(result => {
      setClassificationColors(result);
    });
  }, []);

  const isIntensity = colorType === 0;
  const isHeightmap = colorType === 2;
  const colorMapRequired = isIntensity || isHeightmap;

  return (
    <Drawer
      className={classes.drawer}
      anchor="right"
      open={props.open}
      transitionDuration={0}
      classes={{
        paper: classes.drawerPaper,
      }}
      BackdropProps={{
        classes: {
          root: classes.backdrop
        }
      }}
    >
      <div className={classes.drawerHeader}>
        <IconButton onClick={props.handleClose}>
          {theme.direction === 'rtl' ? <ChevronLeftIcon/> : <ChevronRightIcon/>}
        </IconButton>
      </div>

      <Divider/>

      {/* Point Cloud Controls */}
      <List
        aria-labelledby="nested-list-subheader"
        dense={true}
        className={classes.list}
      >
        <ListItem>
          <Typography className={classes.boldTypography} gutterBottom>
            Point Cloud Controls
          </Typography>
        </ListItem>

        {/* Color type */}
        <ListItem>
          <div className={classes.listItem}>
            <Typography gutterBottom variant="subtitle2">
              Color Type
            </Typography>
            <FormControl className={classes.formControl}>
              <Select
                value={colorType}
                onChange={(event) => {
                  const value = event.target.value as ColorType;
                  dispatch(changeColorType(value));
                }}
              >
                <MenuItem key={0} value={0}>Intensity</MenuItem>
                <MenuItem key={1} value={1}>Color</MenuItem>
                <MenuItem key={2} value={2}>Height</MenuItem>
                <MenuItem key={3} value={3}>Classification</MenuItem>
              </Select>
            </FormControl>
          </div>
        </ListItem>

        {/* Color maps */}
        {colorMapRequired && (<ListItem>
          <div className={classes.listItem}>
            <Typography gutterBottom variant="subtitle2">
              Color Map
            </Typography>
            <FormControl className={classes.formControl}>
              <Select
                value={colorMap}
                renderValue={value => colorMaps.find(colorMap => colorMap.id === value).name}
                onChange={(event) => {
                  const value = event.target.value as ColorMapKey;
                  dispatch(changeColorMap(value));
                }}
              >
                {colorMaps.map(colorMap =>
                  (<MenuItem key={colorMap.id} value={colorMap.id}>
                    <Avatar
                      classes={{img: classes.img}}
                      src={colorMap.image}
                      className={classes.colorPreview}
                    />
                    {colorMap.name}
                  </MenuItem>))
                }
              </Select>
            </FormControl>
          </div>
        </ListItem>)}

        {/* Arrow Markers */}
        <ListItem>
          <div className={classes.listItem}>
            <Typography gutterBottom variant="subtitle2">
              Navigation
            </Typography>
            <FormControl className={classes.formControl}>
              <Select
                value={arrowMarkers}
                onChange={(event) => {
                  let value = event.target.value as MarkerNavType;
                  dispatch(changeArrowMarkers(value));
                }}
              >
                <MenuItem key={0} value={MarkerNavType.Arrows}>Arrows</MenuItem>
                <MenuItem key={1} value={MarkerNavType.Markers}>Markers</MenuItem>
                <MenuItem key={2} value={MarkerNavType.None}>None</MenuItem>
              </Select>
            </FormControl>
          </div>
        </ListItem>

        {/* Cloud Opacity */}
        <ListItem>
          <div className={classes.listItem}>
            <Typography gutterBottom variant="subtitle2">
              Point Visibility
            </Typography>
            <Slider
              color={"secondary"}
              value={opacitySliderValue}
              min={0}
              max={1}
              step={0.05}
              valueLabelDisplay="off"
              onChange={(_, value: number) => {
                updateOpacity(value);
                saveOpacity(value);
              }}
            />
          </div>
        </ListItem>

        {/* Cloud Distance */}
        <ListItem>
          <div className={classes.listItem}>
            <Typography gutterBottom variant="subtitle2">
              Distance
            </Typography>
            <Slider
              color={"secondary"}
              value={distanceSliderValue}
              min={0}
              max={100}
              step={0.5}
              valueLabelDisplay="off"
              onChange={(_, value: number) => {
                updateDistance(value);
                saveCloudDistance(value);
              }}
            />
          </div>
        </ListItem>

        {/* Cloud Size */}
        <ListItem>
          <div className={classes.listItem}>
            <Typography gutterBottom variant="subtitle2">
              Size
            </Typography>
            <Slider
              color={"secondary"}
              value={cloudSizeSlider}
              min={0}
              max={10}
              step={0.1}
              valueLabelDisplay="off"
              onChange={(_, value: number) => {
                updateCloudSize(value);
                saveCloudSize(value);
              }}
            />
          </div>
        </ListItem>

        {/* Height range */}
        {isHeightmap && (<ListItem>
          <div className={classes.listItem}>
            <Typography gutterBottom variant="subtitle2">
              Height Range
            </Typography>
            <Slider
              color={"secondary"}
              value={[heightClipSlider.min, heightClipSlider.max]}
              min={-0.5}
              max={1.5}
              step={0.1}
              valueLabelDisplay="off"
              onChange={(_, value: number[]) => {
                let height = {min: value[0], max: value[1]};
                updateHeightClip(height);
                saveHeightClip(height);
              }}
            />
          </div>
        </ListItem>)}

        {/* Intensity range */}
        {isIntensity && (<ListItem>
          <div className={classes.listItem}>
            <Typography gutterBottom variant="subtitle2">
              Intensity Range
            </Typography>
            <Slider
              color={"secondary"}
              value={[intensityClipSlider.min, intensityClipSlider.max]}
              min={-0.5}
              max={1.5}
              step={0.1}
              valueLabelDisplay="off"
              onChange={(_, value: number[]) => {
                let height = {min: value[0], max: value[1]};
                updateIntensityClip(height);
                saveIntensityClip(height);
              }}
            />
          </div>
        </ListItem>)}

        {/* Dynamic size */}
        <ListItem>
          <div className={classes.listItem}>
            <Typography
              gutterBottom
              variant="subtitle2"
              className={classes.switchLabel}
            >
              Dynamic Point Sizing
            </Typography>

            <Switch
              checked={dynamicSize}
              className={classes.switchElement}
              size="small"
              color="secondary"
              title="Note: This will have no effect for raw las files"
              onChange={(_, checked) => {
                dispatch(changeDynamicSize(checked));
              }}
            />
          </div>
        </ListItem>

        {/* Circular Points */}
        <ListItem>
          <div className={classes.listItem}>
            <Typography
              gutterBottom
              variant="subtitle2"
              className={classes.switchLabel}
            >
              Circular Points
            </Typography>

            <Switch
              checked={circularPoints}
              className={classes.switchElement}
              size="small"
              color="secondary"
              onChange={(_, checked) => {
                dispatch(changeCircularPoints(checked));
              }}
            />
          </div>
        </ListItem>

        {/* Picker Debug */}
        {isDevMode && (<ListItem>
          <div className={classes.listItem}>
            <Typography
              gutterBottom
              variant="subtitle2"
              className={classes.switchLabel}
            >
              Picker Debug
            </Typography>

            <Switch
              className={classes.switchElement}
              size="small"
              color="secondary"
              onChange={(_, checked) => {
                viewer?.togglePickerDebug(checked);
              }}
            />
          </div>
        </ListItem>)}
      </List>

      {(orthos.length > 0) && (<React.Fragment>
        <Divider variant="middle"/>

        {/* Ortho Settings */}
        <List
          aria-labelledby="nested-list-subheader"
          dense={true}
          className={classes.list}
        >
          <ListItem>
            <Typography className={classes.boldTypography} gutterBottom>
              Orthomosaic Settings
            </Typography>
          </ListItem>

          {/* Ortho opacity */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                Opacity
              </Typography>
              <Slider
                color={"secondary"}
                value={orthoOpacitySliderValue}
                min={0}
                max={1}
                step={0.05}
                valueLabelDisplay="off"
                onChange={(_, value: number) => {
                  updateOrthoOpacity(value);
                  saveOrthoOpacity(value);
                }}
              />
            </div>
          </ListItem>
        </List>
      </React.Fragment>)}

      <Divider variant="middle"/>

      {/* Classifications */}
      <List>
        <ListItem>
          <Typography className={classes.boldTypography} gutterBottom>
            Classifications
          </Typography>
        </ListItem>
        {getClassifications(availableClassifications)}
      </List>

    </Drawer>
  );
};
